import { useEffect, useMemo, useState } from "react";
import { Button, message, Space, Switch, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import PvTable from "../../components/Table/PvTable";
import ConfirmDelete from "../../components/ConfirmDelete/ConfirmDelete";
import Popup from "../../components/Popup";
import PvForm from "../../components/Form/PvForm";
import useQuickLinkService from "../../services/SuperAdmin/quickLinkService";
import {
  QUICK_LINK_ICON_OPTIONS,
  renderQuickLinkIcon,
} from "../../utils/quickLinkIcons";

const QuickLinksPage = () => {
  const {
    getAllQuickLinks,
    listLoading,
    listResponse,
    createQuickLink,
    createLoading,
    updateQuickLink,
    updateLoading,
    deleteQuickLink,
  } = useQuickLinkService();

  const [tableData, setTableData] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    getAllQuickLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!listResponse) return;

    const mapped = listResponse.map((item, index) => ({
      key: index + 1,
      id: item.id,
      title: item.title,
      link: item.link,
      short_description: item.short_description,
      icon: item.icon,
      is_pinned: item.is_pinned,
      is_active: item.is_active,
    }));
    setTableData(mapped);
  }, [listResponse]);

  const handleSubmit = async (values) => {
    try {
      if (editing) {
        await updateQuickLink(editing.id, values);
        message.success("Quick link updated.");
      } else {
        await createQuickLink(values);
        message.success("Quick link added.");
      }
      setIsFormOpen(false);
      setEditing(null);
      getAllQuickLinks();
    } catch {
      message.error(
        editing ? "Failed to update quick link." : "Failed to add quick link."
      );
    }
  };

  const handleTogglePinned = async (record, checked) => {
    try {
      await updateQuickLink(record.id, { is_pinned: checked });
      getAllQuickLinks();
    } catch {
      message.error("Failed to update pinned status.");
    }
  };

  const handleToggleActive = async (record, checked) => {
    try {
      await updateQuickLink(record.id, { is_active: checked });
      getAllQuickLinks();
    } catch {
      message.error("Failed to update status.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteQuickLink(id);
      message.success("Quick link removed.");
      getAllQuickLinks();
    } catch {
      message.error("Failed to remove quick link.");
    }
  };

  const columns = [
    { title: "No.", dataIndex: "key", rowScope: "row", width: 60 },
    {
      title: "Icon",
      dataIndex: "icon",
      key: "icon",
      width: 60,
      render: (icon) => renderQuickLinkIcon(icon, { fontSize: "medium" }),
    },
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Short Description", dataIndex: "short_description", key: "short_description" },
    {
      title: "Link",
      dataIndex: "link",
      key: "link",
      render: (value) => (
        <a href={value} target="_blank" rel="noopener noreferrer">
          {value}
        </a>
      ),
    },
    {
      title: "Pinned (Most Used)",
      dataIndex: "is_pinned",
      key: "is_pinned",
      render: (isPinned, record) => (
        <Switch
          checked={isPinned}
          onChange={(checked) => handleTogglePinned(record, checked)}
          style={{ background: isPinned ? "#00573F" : undefined }}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          onChange={(checked) => handleToggleActive(record, checked)}
          style={{ background: isActive ? "#00573F" : undefined }}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              size="small"
              onClick={() => {
                setEditing(record);
                setIsFormOpen(true);
              }}
            >
              Edit
            </Button>
          </Tooltip>
          <ConfirmDelete
            isService={true}
            buttonText="Remove"
            onDeleted={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  const formFields = useMemo(
    () => [
      {
        name: "title",
        label: "Title",
        rules: [{ required: true, message: "Please enter a title." }],
      },
      {
        name: "link",
        label: "Link",
        rules: [
          { required: true, message: "Please enter a link." },
          { type: "url", message: "Please enter a valid URL." },
        ],
      },
      {
        name: "short_description",
        label: "Short Description",
        rules: [{ required: true, message: "Please enter a short description." }],
      },
      {
        name: "icon",
        label: "Icon",
        type: "select",
        selectItems: QUICK_LINK_ICON_OPTIONS,
        rules: [{ required: true, message: "Please select an icon." }],
      },
    ],
    []
  );

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button
          size="large"
          type="primary"
          style={{ background: "#00573F" }}
          icon={<PlusOutlined />}
          onClick={() => {
            setEditing(null);
            setIsFormOpen(true);
          }}
        >
          Add Quick Link
        </Button>
      </Space>

      <PvTable
        data={tableData}
        columns={columns}
        loading={listLoading || updateLoading}
        onRowClick={() => {}}
      />

      <Popup
        title={editing ? "Edit Quick Link" : "Add Quick Link"}
        isOpen={isFormOpen}
        footer={null}
        onCancel={() => {
          setIsFormOpen(false);
          setEditing(null);
        }}
      >
        <PvForm
          isService={true}
          formFields={formFields}
          initialValues={editing}
          loading={createLoading || updateLoading}
          onSubmitted={handleSubmit}
        />
      </Popup>
    </>
  );
};

export default QuickLinksPage;
